import { makeStyles } from "@mui/styles";
import cx from "classnames";
import PropTypes from "prop-types";
import React from "react";
import DatastoreTreeFolder from "./datastore-tree-folder";
import DatastoreTreeItem from "./datastore-tree-item";

const useStyles = makeStyles((theme) => ({
  virtualizedElement: {
    display: "flex",
    flexDirection: "row",
    "&:focus-visible": {
      outline: `3px solid ${theme.palette.text.primary}`,
      outlineOffset: -3,
    },
  },
  nestingDecorator: {
    position: "relative",
    paddingLeft: 22,

    "&:before": {
      display: "inline-block",
      content: '""',
      position: "absolute",
      top: -23,
      bottom: 24,
      left: 9,
      border: `dotted ${theme.palette.blueBackground.main}`,
      borderWidth: "0 0 0 1px",
      zIndex: 1,
    },
  },
  nestingDecoratorFirst: {
    "&:before": {
      top: -10,
    },
  },
}));

const DatastoreTreeVirtualElement = ({ index, style, data }) => {
  const classes = useStyles();

  const {
    items,
    props,
    offline,
    datastore,
    getIsExpandedFolder,
    onUpdateExpandFolderProperty,
  } = data;
  const content = items[index];

  const isExpanded = getIsExpandedFolder(content);

  const isFirstInFolder = items[index - 1]?.path?.length < content.path.length;

  function getNodePathById(id, path, datastore, nodePath, index = -1) {
    if (path[index] && path[index] === datastore.id) {
      nodePath.push(datastore);
    }

    if (datastore.folders) {
      datastore.folders.forEach((item) =>
        getNodePathById(id, path, item, nodePath, index + 1)
      );
    }

    if (datastore.items) {
      datastore.items.forEach((item) =>
        getNodePathById(id, path, item, nodePath, index + 1)
      );
    }

    return nodePath;
  }

  const nodePath = getNodePathById(content.id, content.path, datastore, [], -1);
  const rowRef = React.useRef(null);
  const meta = data.metadata.get(content.id);
  const selectable = !props.isSelectable || props.isSelectable(content);
  React.useLayoutEffect(() => {
    data.consumeFocus(content.id, rowRef.current);
  });

  return (
    <div
      className={classes.virtualizedElement}
      style={style}
      ref={rowRef}
      role="treeitem"
      tabIndex={data.activeId === content.id ? 0 : -1}
      data-code10-tree-node={content.id}
      aria-label={content.name}
      aria-level={meta.level}
      aria-posinset={meta.position}
      aria-setsize={meta.size}
      aria-expanded={meta.expandable ? Boolean(isExpanded) : undefined}
      aria-disabled={!selectable || undefined}
      aria-selected={
        selectable && props.allowMultiselect
          ? Boolean(props.isSelected?.(content))
          : undefined
      }
      onFocus={(event) => {
        if (event.target === event.currentTarget) data.onRowFocus(content.id);
      }}
      onMouseDown={(event) => {
        if (!event.target.closest("button, a, input, [role=menuitem]"))
          event.currentTarget.focus({ preventScroll: true });
      }}
      onKeyDown={(event) => data.onTreeKeyDown(event, index)}
    >
      {content.path.map((item, index, items) => (
        <div
          key={item}
          className={cx({
            [classes.nestingDecorator]: true,
            [classes.nestingDecoratorFirst]:
              isFirstInFolder && index + 1 === items.length,
          })}
        />
      ))}
      <div style={{ flex: 1, minWidth: 0 }}>
        {content.is_folder ? (
          <DatastoreTreeFolder
            isSelectable={props.isSelectable}
            hideItems={props.hideItems}
            onSelectItem={props.onSelectItem}
            isSelected={props.isSelected}
            allowMultiselect={props.allowMultiselect}
            onSelectNode={props.onSelectNode}
            onEditFolder={props.onEditFolder}
            onEditEntry={props.onEditEntry}
            onCloneEntry={props.onCloneEntry}
            onDeleteEntry={props.onDeleteEntry}
            onMoveEntry={props.onMoveEntry}
            onDeleteFolder={props.onDeleteFolder}
            onMoveFolder={props.onMoveFolder}
            onLinkItem={props.onLinkItem}
            onNewFolder={props.onNewFolder}
            onNewUser={props.onNewUser}
            onNewEntry={props.onNewEntry}
            onShare={props.onShare}
            onLinkShare={props.onLinkShare}
            onUpdateExpandFolderProperty={onUpdateExpandFolderProperty}
            key={content.id}
            nodePath={nodePath}
            content={content}
            offline={offline}
            isExpandedDefault={Boolean(isExpanded)}
            deleteFolderLabel={props.deleteFolderLabel}
            deleteItemLabel={props.deleteItemLabel}
          />
        ) : (
          <DatastoreTreeItem
            isSelectable={props.isSelectable}
            onSelectItem={props.onSelectItem}
            isSelected={props.isSelected}
            allowMultiselect={props.allowMultiselect}
            onEditEntry={props.onEditEntry}
            onCloneEntry={props.onCloneEntry}
            onDeleteEntry={props.onDeleteEntry}
            onMoveEntry={props.onMoveEntry}
            onLinkItem={props.onLinkItem}
            onShare={props.onShare}
            onLinkShare={props.onLinkShare}
            key={content.id}
            nodePath={nodePath}
            content={content}
            offline={offline}
            deleteItemLabel={props.deleteItemLabel}
          />
        )}
      </div>
    </div>
  );
};

DatastoreTreeVirtualElement.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.shape({
    items: PropTypes.array.isRequired,
    props: PropTypes.object.isRequired,
    offline: PropTypes.bool.isRequired,
    datastore: PropTypes.object.isRequired,
    onUpdateExpandFolderProperty: PropTypes.func.isRequired,
    getIsExpandedFolder: PropTypes.func.isRequired,
  }).isRequired,
};

export default DatastoreTreeVirtualElement;
